import previewSignaturePlaceholder from 'assets/signature.png'
import fetchAttestationSignatureBlob, {
  Dc3AttestationSignatureType,
} from 'api/administration/dc3Attestations/fetchAttestationSignatureBlob'
import { IDc3CertificateData } from './types'

export interface IDc3SignatureOverrides {
  instructorSignature?: File | Blob | string | null
  legalRepresentativeSignature?: File | Blob | string | null
  workersRepresentativeSignature?: File | Blob | string | null
}

export interface ResolveSignatureForPdfContext {
  attestationId?: number
  signatureType?: Dc3AttestationSignatureType
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const urlToDataUrlViaCanvas = (url: string): Promise<string | null> =>
  new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = image.naturalWidth
        canvas.height = image.naturalHeight
        const context = canvas.getContext('2d')
        if (!context) {
          resolve(null)
          return
        }
        context.drawImage(image, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } catch {
        resolve(null)
      }
    }
    image.onerror = () => resolve(null)
    image.src = url
  })

const fetchAttestationSignatureDataUrl = async (
  attestationId: number,
  signatureType: Dc3AttestationSignatureType
): Promise<string | null> => {
  try {
    const blob = await fetchAttestationSignatureBlob(
      attestationId,
      signatureType
    )
    if (!blob.size) return null

    return blobToDataUrl(blob)
  } catch {
    return null
  }
}

const isBlobLike = (value: unknown): value is Blob =>
  value instanceof Blob ||
  (typeof value === 'object' &&
    value !== null &&
    'arrayBuffer' in value &&
    typeof (value as Blob).arrayBuffer === 'function')

const resolveRemoteSignatureForPdf = async (
  url: string,
  context: ResolveSignatureForPdfContext = {}
): Promise<string | null> => {
  try {
    const response = await fetch(url)
    if (response.ok) {
      return await blobToDataUrl(await response.blob())
    }
  } catch {
    // fetch directo puede fallar en URLs privadas de S3 por CORS.
  }

  const fromCanvas = await urlToDataUrlViaCanvas(url)
  if (fromCanvas) return fromCanvas

  if (context.attestationId && context.signatureType) {
    return fetchAttestationSignatureDataUrl(
      context.attestationId,
      context.signatureType
    )
  }

  return null
}

/** Convierte URL (http, blob, asset) a data URL para html2canvas. */
export const resolveSignatureUrlForPdf = async (
  url: string | null | undefined,
  context: ResolveSignatureForPdfContext = {}
): Promise<string | null> => {
  if (!url) return null
  if (url.startsWith('data:')) return url

  return resolveRemoteSignatureForPdf(url, context)
}

/** Resuelve File/Blob del formulario, URL guardada o placeholder de preview. */
export const resolveSignatureInputForPdf = async (
  override: File | Blob | string | null | undefined,
  attestationUrl: string | null | undefined,
  usePreviewPlaceholder: boolean,
  context: ResolveSignatureForPdfContext = {}
): Promise<string | null> => {
  if (isBlobLike(override)) {
    return blobToDataUrl(override)
  }

  if (typeof override === 'string' && override.trim()) {
    return resolveRemoteSignatureForPdf(override, context)
  }

  if (attestationUrl) {
    return resolveRemoteSignatureForPdf(attestationUrl, context)
  }

  if (usePreviewPlaceholder) {
    return resolveSignatureUrlForPdf(previewSignaturePlaceholder)
  }

  return null
}

export const normalizeCertificateDataForPdf = async (
  data: IDc3CertificateData,
  signatureOverrides: IDc3SignatureOverrides = {},
  usePreviewPlaceholder = false,
  attestationId?: number
): Promise<IDc3CertificateData> => {
  const { attestation } = data
  const resolvedAttestationId = attestationId ?? attestation.id

  const [
    instructorSignatureUrl,
    legalRepresentativeSignatureUrl,
    workersRepresentativeSignatureUrl,
  ] = await Promise.all([
    resolveSignatureInputForPdf(
      signatureOverrides.instructorSignature,
      attestation.instructorSignatureUrl,
      usePreviewPlaceholder,
      { attestationId: resolvedAttestationId, signatureType: 'instructor' }
    ),
    resolveSignatureInputForPdf(
      signatureOverrides.legalRepresentativeSignature,
      attestation.legalRepresentativeSignatureUrl,
      usePreviewPlaceholder,
      {
        attestationId: resolvedAttestationId,
        signatureType: 'legal_representative',
      }
    ),
    resolveSignatureInputForPdf(
      signatureOverrides.workersRepresentativeSignature,
      attestation.workersRepresentativeSignatureUrl,
      usePreviewPlaceholder,
      {
        attestationId: resolvedAttestationId,
        signatureType: 'workers_representative',
      }
    ),
  ])

  return {
    ...data,
    attestation: {
      ...attestation,
      instructorSignatureUrl,
      legalRepresentativeSignatureUrl,
      workersRepresentativeSignatureUrl,
    },
  }
}
